import React, { useState, useMemo, useEffect } from 'react';
import MaterialTable from 'material-table';
import EditIcon from '@material-ui/icons/Edit';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { NativeSelect } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import Pagination from 'react-js-pagination';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import Header from '../../layout/Header';
import Subheader from '../../layout/Subheader';
import Loading from '../../components/Loading';
import { findPermission, TOKEN_PAGINATION } from '../../services/auth';
import { consultar } from '../../services/erpsService';
import { useMessageBox } from '../../context/MessageBox';
import useStyles from './style';

// Permissoes ModuloId=36842 (ImportacoesTitulosApisErps — dbo.fnCalculaModuloId).
// PermissaoId = ModuloId*100 + suffix (01 Cadastrar, 02 Alterar, 03 Remover, 04 Consultar).
const MODULO_ID = 36842;

export function ERPs() {
  const classes = useStyles();
  const { setMessageData } = useMessageBox();
  const [isLoading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('Ativo');
  const [erp, setErp] = useState('');
  const [versao, setVersao] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itensPerPage, setItensPerPage] = useState(50);
  const [pagination, setPagination] = useState({});
  const [openModalFilter, setModalFilter] = useState(false);

  const memoizedTable = useMemo(
    () => (
      <MaterialTable
        className={classes.table}
        options={{
          showTitle: false,
          search: false,
          toolbar: false,
          headerStyle: {
            backgroundColor: '#F5F5F5',
            textTransform: 'none',
            fontSize: '15px',
          },
          filtering: true,
          paging: false,
          showEmptyDataSourceMessage: false,
        }}
        onRowClick={(event, rowData) => {
          if (findPermission(MODULO_ID, 'Alterar')) {
            window.location.href =
              window.location.origin +
              `/app/erp/${rowData.importacaoTituloApiErpId}/editar`;
          }
        }}
        columns={[
          {
            title: 'ERP',
            field: 'erp',
            cellStyle: { fontSize: '13px', padding: '8px 16px' },
          },
          {
            title: 'Versão',
            field: 'versao',
            cellStyle: { fontSize: '13px', padding: '8px 16px' },
          },
          {
            title: 'URL Base',
            field: 'urlBase',
            cellStyle: { fontSize: '13px', padding: '8px 16px' },
          },
          {
            title: 'Autenticação',
            field: 'tipoAutenticacao',
            cellStyle: { fontSize: '13px', padding: '8px 16px' },
          },
          {
            title: 'Status',
            field: 'status',
            cellStyle: { fontSize: '13px', padding: '8px 10px', width: '150px' },
          },
          {
            title: 'Ações',
            field: 'actions',
            headerStyle: { textAlign: 'center' },
            cellStyle: {
              fontSize: '13px',
              textAlign: 'center',
              padding: '8px 16px',
            },
            filtering: false,
            sorting: false,
            disableClick: true,
            render: (rowData) => (
              <>
                {!!findPermission(MODULO_ID, 'Consultar') && (
                  <Link
                    className={classes.buttonVisualize}
                    to={`/app/erp/${rowData.importacaoTituloApiErpId}/visualizar`}
                  >
                    <VisibilityIcon style={{ fontSize: 30, color: '#4AAF05' }} />
                  </Link>
                )}
                {!!findPermission(MODULO_ID, 'Alterar') && (
                  <Link
                    className={classes.buttonEdit}
                    to={`/app/erp/${rowData.importacaoTituloApiErpId}/editar`}
                  >
                    <EditIcon style={{ fontSize: 30, color: '#FB8832' }} />
                  </Link>
                )}
              </>
            ),
          },
        ]}
        data={items}
      />
    ),
    [items],
  );

  const fetchERPs = async (pageNumber = currentPage) => {
    setLoading(true);
    try {
      const res = await consultar({
        paginaInicial: pageNumber,
        itensPorPagina: itensPerPage,
        status,
        erp,
        versao,
      });

      if (res?.data?.complemento?.mensagensBox?.length > 0) {
        setMessageData(res.data.complemento.mensagensBox);
      }

      if (res.data.status !== 'Sucesso') {
        if (res.data.resposta?.length > 0) {
          res.data.resposta.forEach((el) => toast.error(el.message));
        }
        setItems([]);
        setPagination({});
        return;
      }

      setPagination(res.data.paginacao || {});
      if (res.data.paginacao?.qtdTotalRegistros > 0) {
        setItems(res.data.resposta || []);
      } else {
        setItems([]);
      }
    } catch (err) {
      toast.error('Detectamos um problema interno! tente novamente em instantes.');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    fetchERPs(pageNumber);
  };

  useEffect(() => {
    const userPage = JSON.parse(sessionStorage.getItem(TOKEN_PAGINATION))
      ?.paginacao?.itensPorPagina;
    setItensPerPage(userPage === 0 || !userPage ? 50 : userPage);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    const t = setTimeout(() => fetchERPs(1), 800);
    return () => clearTimeout(t);
  }, [itensPerPage]);

  return (
    <div className={classes.root}>
      <Header />
      <Subheader title='Perfis ERP' back='/app/cadastros' />
      <div className={classes.content}>
        <div className={classes.tableRoot}>
          <div className={classes.actions}>
            <button
              type='button'
              onClick={() => setModalFilter(!openModalFilter)}
              className={classes.button}
            >
              <span>Filtros</span>
            </button>
            {!!findPermission(MODULO_ID, 'Cadastrar') && (
              <Link className={classes.button} to='/app/erp/cadastrar'>
                Novo ERP
              </Link>
            )}
            {openModalFilter && (
              <div className={classes.filtersBox}>
                <div className={classes.headFilter}>
                  <span className={classes.labelFilter}>Filtros</span>
                  <button
                    className={classes.buttonClose}
                    onClick={() => setModalFilter(false)}
                  >
                    <CloseIcon style={{ fontSize: 25, color: '#fff' }} />
                  </button>
                </div>
                <span className={classes.labelFilter}>Status</span>
                <select
                  onChange={(e) => setStatus(e.target.value)}
                  value={status}
                  className={classes.selectStatus}
                >
                  <option value=''>Todos</option>
                  <option value='Ativo'>Ativo</option>
                  <option value='Inativo'>Inativo</option>
                </select>
                <span className={classes.labelFilter}>ERP</span>
                <input
                  type='text'
                  value={erp}
                  onChange={(e) => setErp(e.target.value)}
                  className={classes.inputFilter}
                  placeholder='Ex.: Bluesoft'
                />
                <span className={classes.labelFilter}>Versão</span>
                <input
                  type='text'
                  value={versao}
                  onChange={(e) => setVersao(e.target.value)}
                  className={classes.inputFilter}
                  placeholder='Ex.: v1'
                />
                <button
                  className={classes.buttonFilter}
                  onClick={() => fetchERPs(1)}
                >
                  Aplicar Filtro
                </button>
              </div>
            )}
          </div>
          {isLoading ? (
            <Loading />
          ) : (
            <>
              {memoizedTable}
              <div className='footer-table'>
                <Pagination
                  activePage={currentPage}
                  itemsCountPerPage={itensPerPage}
                  totalItemsCount={pagination.qtdTotalRegistros || 0}
                  pageRangeDisplayed={5}
                  onChange={handlePageChange}
                />
                <div className='select-page'>
                  <label>Qtd. por página</label>
                  <NativeSelect
                    onChange={(e) => setItensPerPage(Number(e.currentTarget.value))}
                    value={itensPerPage}
                  >
                    <option value='25'>25</option>
                    <option value='50'>50</option>
                    <option value='100'>100</option>
                    <option value='200'>200</option>
                  </NativeSelect>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
